import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GoogleLogin } from "@react-oauth/google";

// ─── Password Strength Helper ─────────────────────────────────────────────────
  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const levels = [
      { score: 0, label: "", color: "" },
      { score: 1, label: "Weak", color: "#ef4444" },
      { score: 2, label: "Fair", color: "#f97316" },
      { score: 3, label: "Good", color: "#eab308" },
      { score: 4, label: "Strong", color: "#22c55e" },
    ];
    return levels[score];
  };

// ─── OTP Modal ────────────────────────────────────────────────────────────────
const OTPModal = ({ mobile, onVerify, onClose, onResend }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [verifying, setVerifying] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  const handleChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      refs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Enter all 6 digits"); return; }
    setVerifying(true);
    await onVerify(code);
    setVerifying(false);
  };

  const handleResend = () => {
    setTimer(30);
    setOtp(["", "", "", "", "", ""]);
    onResend();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", justifyContent: "center", alignItems: "center",
      zIndex: 9999, backdropFilter: "blur(3px)"
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, padding: "32px 28px",
        width: 320, textAlign: "center", position: "relative",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 10, right: 14, background: "none",
          border: "none", fontSize: 18, cursor: "pointer", color: "#999"
        }}>✕</button>

        <div style={{ fontSize: 36, marginBottom: 8 }}>📱</div>
        <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700 }}>Verify Mobile</h3>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#666" }}>
          OTP sent to <strong>+91 {mobile}</strong>
        </p>

        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 20 }} onPaste={handlePaste}>
          {otp.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (refs.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, idx)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              style={{
                width: 40, height: 46, textAlign: "center", fontSize: 20,
                fontWeight: 700, border: `2px solid ${digit ? "#ff3f6c" : "#ddd"}`,
                borderRadius: 8, outline: "none",
                background: digit ? "#fff0f3" : "#fff"
              }}
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          disabled={verifying}
          className="continue"
          style={{ width: "100%", marginBottom: 12 }}
        >
          {verifying ? "Verifying…" : "Verify OTP"}
        </button>

        <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
          {timer > 0 ? (
            <span>Resend in {timer}s</span>
          ) : (
            <button onClick={handleResend} style={{
              background: "none", border: "none", color: "#ff3f6c",
              fontWeight: 700, cursor: "pointer", fontSize: 13
            }}>Resend OTP</button>
          )}
        </p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Signin = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    password: "",
    address: "",
    address_2: "",
    state: "",
    city: "",
    area: "",
    country: "India",
    pincode: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: "", color: "" });
  const [mobileVerified, setMobileVerified] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    getLocation();
    const saved = JSON.parse(localStorage.getItem("savedAddresses") || "[]");
    setAddressSuggestions(saved);
  }, []);

  const getLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      try {
        const res = await axios.get(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
        );
        setFormData((prev) => ({
          ...prev,
          city: res.data.city || prev.city,
          state: res.data.principalSubdivision || prev.state,
          country: "India",
        }));
      } catch (err) {
        console.log("Location fetch error");
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password") {
      setPasswordStrength(getPasswordStrength(value));
    }
  };

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData((prev) => ({ ...prev, pincode: pin }));
    if (pin.length === 6) {
      try {
        const res = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
        if (res.data[0].Status === "Success") {
          const data = res.data[0].PostOffice[0];
          setFormData((prev) => ({
            ...prev,
            city: data.District,
            state: data.State,
            country: "India",
            area: data.Name,
          }));
          toast.success("Location auto-filled ✓");
        }
      } catch (err) {
        console.log("Pincode fetch error");
      }
    }
  };

  // ── OTP ──────────────────────────────────────────────────────────────────────
  const sendOTP = async () => {
    if (!formData.mobile || formData.mobile.length !== 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    // TODO: Call your OTP API here e.g. MSG91 / Fast2SMS
    // await axios.post('/api/send-otp', { mobile: formData.mobile });
    toast.info(`OTP sent to +91 ${formData.mobile}`);
    setShowOTP(true);
  };

  const verifyOTP = async (code) => {
    // TODO: Replace with real OTP verification API call
    // const res = await axios.post('/api/verify-otp', { mobile: formData.mobile, otp: code });
    if (code.length === 6) {
      setMobileVerified(true);
      setShowOTP(false);
      toast.success("Mobile verified ✓");
    } else {
      toast.error("Incorrect OTP. Try again.");
    }
  };

  // ── Address Suggestion ───────────────────────────────────────────────────────
  const pickSuggestion = (addr) => {
    setFormData((prev) => ({ ...prev, address: addr }));
    setShowSuggestions(false);
  };

  const handleGoogleLogin = async (credentialResponse) => {
  try {
    setSubmitting(true);

    console.log("GOOGLE ID TOKEN:", credentialResponse.credential);

    const response = await axios.post(
      "https://cyberathon.com/streekatha/API/index.php/Master_controller/google_login",
      {
        token: credentialResponse.credential,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Google Login Response:", response.data);

    if (response.data.success === true) {
      toast.success(
        response.data.message || "Login successful via Google"
      );

      // Your API returns user inside data.user
      const user = response.data.data?.user;

      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }

      // If backend returns your application's token
      if (response.data.data?.token) {
        localStorage.setItem("token", response.data.data.token);
      }

      // Redirect after successful login
      setTimeout(() => {
        navigate("/");
      }, 500);
    } else {
      toast.error(
        response.data.message || "Google login failed"
      );
    }
  } catch (error) {
    console.error("Google login error:", error);
    console.error("Backend response:", error.response?.data);

    toast.error(
      error.response?.data?.message ||
      "Google login failed"
    );
  } finally {
    setSubmitting(false);
  }
};

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!mobileVerified) {
      toast.error("Please verify your mobile number first");
      return;
    }
    if (!formData.mobile || !formData.password) {
      toast.error("Mobile number and password are required");
      return;
    }

    setSubmitting(true);
    try {
      // Save address suggestion
      if (formData.address) {
        const saved = JSON.parse(localStorage.getItem("savedAddresses") || "[]");
        const updated = Array.from(new Set([formData.address, ...saved])).slice(0, 5);
        localStorage.setItem("savedAddresses", JSON.stringify(updated));
      }

      const response = await axios.post(
        "https://cyberathon.com/streekatha/API/index.php/Master_controller/add_user",
        formData,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === "1") {
        toast.success("Account created successfully");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        toast.error(response.data.message || "Signup failed");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const strength = passwordStrength;

  return (
    <div className="container-fluid login-section">
      <div id="loginbox">
        <img
          src="https://assets.myntassets.com/f_webp,dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2021/7/14/1db28809-f3db-48b1-b303-d937716565661626257521634-Banner_Hamburger--2---1-.jpg"
          alt="Signup Banner"
        />

        <div className="loginbody">
          <div className="loginheader">
            Signup <span id="smallheader">Create Account</span>
          </div>

          <form onSubmit={handleSubmit} id="login">

  {/* Google Login */}
  <div style={{ marginBottom: 20 }}>
    <GoogleLogin
  onSuccess={(credentialResponse) => {
    console.log("GOOGLE ID TOKEN:", credentialResponse.credential);
    handleGoogleLogin(credentialResponse);
  }}
  onError={() => {
    console.log("Google Login Failed");
  }}
/>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "15px 0",
      color: "#999",
      fontSize: 13,
    }}
  >
    <div style={{ flex: 1, height: 1, background: "#ddd" }} />
    <span>OR</span>
    <div style={{ flex: 1, height: 1, background: "#ddd" }} />
  </div>

  {/* Full Name */}

            {/* Full Name */}
            <label>Full Name</label>
            <input
              type="text"
              name="name"
              placeholder="Enter your name"
              value={formData.name}
              onChange={handleChange}
            />

            {/* Mobile + OTP */}
            <label>Mobile Number</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
  type="text"
  name="mobile"
  placeholder="Enter 10-digit mobile number"
  value={formData.mobile}
  onChange={(e) => {
    const mobile = e.target.value.replace(/\D/g, "").slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      mobile,
    }));

    // If user changes the number after verification
    setMobileVerified(false);
  }}
  maxLength={10}
  inputMode="numeric"
  required
  style={{ flex: 1 }}
/>
              <button
                type="button"
                onClick={sendOTP}
                className="continue"
                style={{
                  width: "auto",
                  padding: "10px 14px",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  background: mobileVerified ? "#22c55e" : undefined,
                }}
              >
                {mobileVerified ? "✓ Verified" : "Send OTP"}
              </button>
            </div>

            {/* Email */}
            <label>Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={handleChange}
            />

            {/* Password + show/hide + strength */}
            <label>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Create password"
                value={formData.password}
                onChange={handleChange}
                required
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)", background: "none",
                  border: "none", cursor: "pointer", fontSize: 17, lineHeight: 1,
                }}
                aria-label="Toggle password visibility"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>

            {/* Password strength bar */}
            {formData.password && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, marginBottom: 4 }}>
                <div style={{ display: "flex", gap: 4, flex: 1 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} style={{
                      flex: 1, height: 4, borderRadius: 4,
                      background: i <= strength.score ? strength.color : "#e2e8f0",
                      transition: "background 0.3s"
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: strength.color, minWidth: 44 }}>
                  {strength.label}
                </span>
              </div>
            )}

            {/* Address Line 1 + suggestions */}
            <label>Address Line 1</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                name="address"
                placeholder="Enter address"
                value={formData.address}
                onChange={(e) => {
  handleChange(e);

  const value = e.target.value.toLowerCase();

  const saved = JSON.parse(localStorage.getItem("savedAddresses") || "[]");

  const filtered = saved.filter((addr) =>
    addr.toLowerCase().includes(value)
  );

  setAddressSuggestions(filtered);
  setShowSuggestions(true);
}}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <div style={{
                  position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0,
                  background: "#fff", border: "1px solid #ddd", borderRadius: 8,
                  zIndex: 100, boxShadow: "0 6px 20px rgba(0,0,0,0.1)", overflow: "hidden"
                }}>
                  <p style={{ margin: 0, padding: "7px 12px 3px", fontSize: 11, color: "#999", fontWeight: 700, textTransform: "uppercase" }}>
                    Previously used
                  </p>
                  {addressSuggestions.map((addr, i) => (
                    <div
                      key={i}
                      onClick={() => pickSuggestion(addr)}
                      style={{
                        padding: "9px 12px", fontSize: 13, color: "#333",
                        cursor: "pointer", borderTop: "1px solid #f0f0f0"
                      }}
                    >
                      📍 {addr}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Address Line 2 */}
            <label>Address Line 2</label>
            <input
              type="text"
              name="address_2"
              placeholder="Enter address"
              value={formData.address_2}
              onChange={handleChange}
            />

            {/* State */}
            <label>State</label>
            <input
              type="text"
              name="state"
              placeholder="Enter state"
              value={formData.state}
              onChange={handleChange}
            />

            {/* City */}
            <label>City</label>
            <input
              type="text"
              name="city"
              placeholder="Enter city"
              value={formData.city}
              onChange={handleChange}
            />

            {/* Area */}
            <label>Area</label>
            <input
              type="text"
              name="area"
              placeholder="Enter area"
              value={formData.area}
              onChange={handleChange}
            />

            {/* Country — auto-fixed India, read-only */}
            <label>Country</label>
            <input
              type="text"
              name="country"
              value="India 🇮🇳"
              readOnly
              style={{ background: "#f5f5f5", color: "#888", cursor: "not-allowed" }}
            />

            {/* Pincode */}
            <label>Pincode</label>
            <input
              type="text"
              name="pincode"
              placeholder="Enter pincode"
              value={formData.pincode}
              onChange={handlePincodeChange}
              maxLength={6}
              inputMode="numeric"
            />

            <button type="submit" className="continue" disabled={submitting}>
              {submitting ? "Creating…" : "Create My Account"}
            </button>
          </form>

          <p>
            Already have an account?{" "}
            <Link to="/login">
              <span>Login</span>
            </Link>
          </p>
        </div>
      </div>

      {/* OTP Modal */}
      {showOTP && (
        <OTPModal
          mobile={formData.mobile}
          onVerify={verifyOTP}
          onClose={() => setShowOTP(false)}
          onResend={sendOTP}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default Signin;